import { Box, Button, LinearProgress, Stack } from "@mui/material";

import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { useFindSubjectByIdSubjectsIdGetQuery } from "api";
import { SubjectPermissionEnum, ZonePermissionEnum } from "authorization/types";
import { ErrorPage, NoDataPage } from "components/pages";
import { SectionCard } from "components/SectionCard/SectionCard";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { IZone } from "interfaces/zone.interface";

import { SubjectDevices } from "./components/SubjectDevices";
import { DeleteSubjectDialog } from "./dialogs/DeleteSubjectDialog";
import { SubjectEditForm } from "./forms/SubjectEditForm";

import NotificationRulesAndLogs from "../components/NotificationRulesAndLogs";
import { PageHeader } from "../components/PageHeader";

const Grid = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(2),
  gridTemplateColumns: "1fr",
  [theme.breakpoints.up("lg")]: {
    gridTemplateColumns: "1fr 350px",
  },
}));

type SubjectEditPageProps = {
  zones: IZone[];
};

export const SubjectEditPage = ({ zones }: SubjectEditPageProps) => {
  const { t } = useTranslation();
  const { subjectId } = useParams<{ subjectId: string }>();
  const { permissions } = useAuthenticatedUser();

  const { data, isLoading, isError, refetch } =
    useFindSubjectByIdSubjectsIdGetQuery(
      {
        id: subjectId ?? "",
        embed: ["devices"],
      },
      { skip: !subjectId },
    );

  const subject = data?.data;

  const hasSleepHistoryPermission = () => {
    return (
      permissions.includes(ZonePermissionEnum["zone.sleep_history"]) ||
      permissions.includes(ZonePermissionEnum["zone.live_monitor"])
    );
  };

  const disableUpdate = () => {
    return !permissions.includes(SubjectPermissionEnum["subject.update"]);
  };
  if (isLoading) {
    return <LinearProgress variant={"indeterminate"} />;
  }

  if (isError) {
    return (
      <ErrorPage
        title={t("Failed to load data")}
        buttonText={t("Try again")}
        buttonOnClick={refetch}
      />
    );
  }

  if (!subject) {
    return <NoDataPage title={t("no_subject_found")} />;
  }

  const zone = zones.find((z) => z.id === subject.zone_id);

  return (
    <Stack gap={2}>
      <PageHeader title={subject.identifier} />
      <Grid>
        <SubjectEditForm
          subject={subject}
          zones={zones}
          disabled={disableUpdate()}
        />

        <SubjectDevices
          zoneId={subject?.zone_id ?? ""}
          devices={subject?.devices?.data || []}
        />
      </Grid>
      <Box display={"flex"} flexDirection={"column"} gap={2}>
        {hasSleepHistoryPermission() && (
          <SectionCard title={t("Sleep history")}>
            <Button component={Link} to={`/subject/${subjectId}/sessions`}>
              {t("view_this_users_sleep_history")}
            </Button>
          </SectionCard>
        )}

        {subjectId && zone && (
          <NotificationRulesAndLogs
            type={"subject"}
            entityId={subjectId}
            zone={zone}
            zones={zones}
          />
        )}

        {!disableUpdate() && (
          <DeleteSubjectDialog
            subjectId={subject.id}
            zoneId={subject.zone_id ?? ""}
          />
        )}
      </Box>
    </Stack>
  );
};
